import HeroSearch from "../components/HeroSearch";
import PricingBlock from "../components/PricingBlock";
import TrustStatement from "../components/TrustStatement";
import { Link } from "react-router-dom";
import { FileText, Mail, Scale, Globe2, Building2, ArrowRight, CheckCircle2, Users, PiggyBank } from "lucide-react";
import { EditableText } from "../lib/content";
import { useSeo } from "../lib/useSeo";

const INCLUDED = [
  { icon: Scale,     key_title: "product.feature1.title", default_title: "Up to £10,000 support",                                key_body: "product.feature1.body", default_body: "If a competitor copies your name, you have real legal backing. Up to £10,000 of passing-off legal assistance is included on every plan." },
  { icon: FileText,  key_title: "product.feature2.title", default_title: "Evidence of use from day one & a dated trading record", key_body: "product.feature2.body", default_body: "We log and timestamp your business name, address and trading history from the day you subscribe. The bedrock of any future passing-off claim." },
  { icon: Users,     key_title: "product.feature3.title", default_title: "UK support team",                                      key_body: "product.feature3.body", default_body: "Phone and email support from a real UK team. No bots, no chasing, no jargon." },
  { icon: Globe2,    key_title: "product.feature4.title", default_title: "Domain guidance",                                      key_body: "product.feature4.body", default_body: "We help you understand which domains to secure around your name and which can wait. Plain advice from a real UK team." },
  { icon: Building2, key_title: "product.feature5.title", default_title: "Conflict awareness",                                   key_body: "product.feature5.body", default_body: "Knowing what is on the Companies House register matters. Our free name checker plus your own oversight keep you aware of new conflicts." },
  { icon: PiggyBank, key_title: "product.feature6.title", default_title: "Affordable pricing",                                   key_body: "product.feature6.body", default_body: "From £15 per month including VAT. No solicitor fees. No surprise bills. Cancel anytime." },
];

const USE_CASES = [
  { key_who: "product.use1.who", default_who: "Trades & services",       key_line: "product.use1.body", default_line: "Plumbers, electricians, roofers, builders. Your name is what gets you found locally." },
  { key_who: "product.use2.who", default_who: "Hair & beauty",            key_line: "product.use2.body", default_line: "Salons, barbers, mobile therapists. Customers refer by name, so protect it." },
  { key_who: "product.use3.who", default_who: "Driving instructors",      key_line: "product.use3.body", default_line: "Booked through Google and word of mouth. Lookalike names cost real bookings." },
  { key_who: "product.use4.who", default_who: "Cafés & restaurants",      key_line: "product.use4.body", default_line: "Food-business names travel fast online. Stop confusion before reviews get mixed up." },
  { key_who: "product.use5.who", default_who: "Childcare & nurseries",    key_line: "product.use5.body", default_line: "Trust is everything. A lookalike name in your area can be catastrophic for enquiries." },
  { key_who: "product.use6.who", default_who: "Independent retail",       key_line: "product.use6.body", default_line: "From florists to bakers, your name is your shop window, online and off." },
];

export default function Product() {
  useSeo({
    title: "What is Business Name Protection",
    description: "A practical, affordable layer of protection for your UK trading name. Up to £10,000 of legal assistance, dated proof of use, real UK support - from £15/mo.",
  });

  return (
    <main data-testid="product-page">
      {/* HERO */}
      <section className="relative overflow-hidden bnp-radial-glow pt-14 md:pt-20 pb-16">
        <div className="absolute inset-0 bnp-grid-bg opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 relative">
          <div className="text-center max-w-3xl mx-auto">
            <span className="bnp-chip bnp-chip-teal">
              <EditableText keyName="product.hero.eyebrow" default="The benefits" />
            </span>
            <h1 className="font-display text-[clamp(2rem,4vw,3.4rem)] text-[#04273d] mt-4 font-semibold tracking-tight">
              <EditableText keyName="product.hero.title" default="Protect your business name before a problem arises" />
            </h1>
            <p className="text-[#447694] text-base md:text-lg mt-5 leading-relaxed">
              <EditableText keyName="product.hero.body" default="Build evidence of use, access support if a dispute occurs, and gain peace of mind from just £15 per month." />
            </p>
            <div
              className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-full bg-[#71edc9]/25 border border-[#71edc9]/50"
              data-testid="product-hero-trading-banner"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#0d9b80] animate-pulse" />
              <span className="text-[12.5px] md:text-sm font-medium text-[#04273d]">
                <EditableText
                  keyName="product.hero.banner"
                  default="Already trading? Protection can usually be set up in minutes."
                />
              </span>
            </div>
            <div className="mt-7 max-w-2xl mx-auto">
              <HeroSearch inline />
            </div>
            <TrustStatement className="mt-6" />
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28" data-testid="product-included">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="max-w-xl mb-10">
            <span className="bnp-chip bnp-chip-light">
              <EditableText keyName="product.included.eyebrow" default="What's included" />
            </span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-semibold tracking-tight">
              <EditableText keyName="product.included.title" default="Six benefits. One simple monthly fee." />
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {INCLUDED.map((f, i) => (
              <div key={i} className="bnp-card p-6 hover:-translate-y-1 transition-transform" data-testid={`product-feature-${i}`}>
                <div className="h-10 w-10 rounded-xl bg-[#71edc9]/30 flex items-center justify-center">
                  <f.icon size={18} className="text-[#04273d]" />
                </div>
                <h3 className="font-display text-lg text-[#04273d] mt-4 font-semibold">
                  <EditableText keyName={f.key_title} default={f.default_title} />
                </h3>
                <p className="text-sm text-[#447694] mt-2 leading-relaxed">
                  <EditableText keyName={f.key_body} default={f.default_body} />
                </p>
              </div>
            ))}
          </div>

          {/* CASE STUDY VIDEO - sits right after the benefit cards so visitors see the benefits proven by a real customer */}
          <div className="mt-14 md:mt-20 max-w-4xl mx-auto" data-testid="product-casestudy">
            <div className="text-center max-w-2xl mx-auto mb-6">
              <span className="bnp-chip bnp-chip-teal">
                <EditableText keyName="product.casestudy.eyebrow" default="Customer story" />
              </span>
              <h2 className="font-display text-[clamp(1.5rem,2.8vw,2.4rem)] text-[#04273d] mt-3 font-semibold tracking-tight">
                <EditableText keyName="product.casestudy.title" default="See these benefits in action." />
              </h2>
              <p className="text-[#447694] mt-3 leading-relaxed">
                <EditableText keyName="product.casestudy.body" default="A real UK business on why protecting their name mattered - and what it means for their peace of mind." />
              </p>
            </div>
            <div className="relative w-full rounded-3xl overflow-hidden bnp-soft-shadow border border-[#9ac7f7]/25" style={{ paddingBottom: "56.25%" }}>
              <iframe
                src="https://www.youtube.com/embed/tSETxrbR9-c?rel=0"
                title="Business Name Protection customer story"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="product-casestudy-video-iframe"
              />
            </div>
          </div>
        </div>
      </section>

      {/* WHO ITS FOR */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28 bg-white border-y border-[#9ac7f7]/25" data-testid="product-use-cases">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="max-w-xl mb-10">
            <span className="bnp-chip bnp-chip-light">
              <EditableText keyName="product.audience.eyebrow" default="Who it's for" />
            </span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-semibold tracking-tight">
              <EditableText keyName="product.audience.title" default="Built for regional UK businesses." />
            </h2>
            <p className="text-[#447694] mt-3 max-w-xl">
              <EditableText keyName="product.audience.body" default="If your customers know you by name first, you need this. From local trades to independent retailers, 2000+ UK businesses already use Business Name Protection." />
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {USE_CASES.map((u, i) => (
              <div key={i} className="bnp-card p-6" data-testid={`product-usecase-${i}`}>
                <p className="font-display font-semibold text-[#04273d]">
                  <EditableText keyName={u.key_who} default={u.default_who} />
                </p>
                <p className="text-sm text-[#447694] mt-1.5 leading-relaxed">
                  <EditableText keyName={u.key_line} default={u.default_line} />
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AFFORDABILITY / EASE */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28" data-testid="product-value">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="bnp-card p-8 md:p-10">
            <span className="bnp-chip bnp-chip-light">
              <EditableText keyName="product.value.eyebrow" default="Why our customers stay" />
            </span>
            <h2 className="font-display text-2xl md:text-3xl text-[#04273d] mt-3 font-semibold tracking-tight">
              <EditableText keyName="product.value.title" default="Affordable. Practical. Honest." />
            </h2>
            <div className="grid md:grid-cols-3 gap-6 mt-6">
              <div>
                <PiggyBank size={22} className="text-[#71edc9]" />
                <h3 className="font-display font-semibold text-[#04273d] mt-3">
                  <EditableText keyName="product.value.col1.title" default="Affordable" />
                </h3>
                <p className="text-sm text-[#447694] mt-1.5">
                  <EditableText keyName="product.value.col1.body" default="From £15 per month including VAT. £180 a year for protection that pays for itself the first time something goes wrong." />
                </p>
              </div>
              <div>
                <CheckCircle2 size={22} className="text-[#71edc9]" />
                <h3 className="font-display font-semibold text-[#04273d] mt-3">
                  <EditableText keyName="product.value.col2.title" default="Easy to set up" />
                </h3>
                <p className="text-sm text-[#447694] mt-1.5">
                  <EditableText keyName="product.value.col2.body" default="Set up in 90 seconds online. No forms in the post, no lawyer involved, no jargon to wade through." />
                </p>
              </div>
              <div>
                <Mail size={22} className="text-[#71edc9]" />
                <h3 className="font-display font-semibold text-[#04273d] mt-3">
                  <EditableText keyName="product.value.col3.title" default="A real UK team" />
                </h3>
                <p className="text-sm text-[#447694] mt-1.5">
                  <EditableText keyName="product.value.col3.body" default="When you have questions, a real person answers. Based in the UK, working with UK businesses." />
                </p>
              </div>
            </div>
            <p className="text-sm text-[#447694] mt-7">
              <EditableText keyName="product.value.body" default="Business Name Protection is a sensible, complementary layer alongside any other steps you take. Many of our customers also explore trade marks once they grow. We'll happily talk you through what makes sense for your business." />
            </p>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-16 md:py-24 lg:py-28 xl:py-32 bg-white border-y border-[#9ac7f7]/25" data-testid="product-pricing">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="max-w-2xl mb-10">
            <span className="bnp-chip bnp-chip-light">
              <EditableText keyName="product.pricing.eyebrow" default="Pricing" />
            </span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-semibold tracking-tight">
              <EditableText keyName="product.pricing.title" default="One simple monthly fee." />
            </h2>
            <p className="text-[#447694] mt-3">
              <EditableText keyName="product.pricing.body" default="VAT included. No surprises. Cancel anytime on monthly." />
            </p>
          </div>
          <PricingBlock />
        </div>
      </section>
    </main>
  );
}
